// @flow
import React from 'react';
import injectSheet from 'react-jss';
import moment from 'moment';
import _ from 'shared/lib/gettext';
import TimePicker from 'rc-time-picker';
import Button from '~/app/components/Button';
import DialogModal from '~/app/components/DialogModal';
import TextInput from '~/app/components/TextInput';
import { S, DARK } from '~/app/ui/tokens';
import CalendarPicker from '~/pages/components/PageEditor/CalendarPicker';
import 'rc-time-picker/assets/index.css';
import { getMinimumDeploymentIntervalLimitHrs } from 'shared/lib/clientSettings';
import getDisplayPath from 'shared/lib/getDisplayPath';

// types
import type Moment from 'moment';

const LIMIT_HRS = getMinimumDeploymentIntervalLimitHrs();
const DEFAULT_BUFFER_HOURS = 2; // set default time further from limit time

type Props = {
  scheduledId: string,
  pages: Array,
  versionNumber: string,
  userId: string,
  defaultDate: Moment,
  position: Object,
  ctaText: string,
  onCancel: () => void,
  onConfirm: () => void
}

type State = {
  date: Moment,
  errors: string
};

class Scheduler extends React.Component<Props, State> {
  static defaultProps = {
    ctaText: _('SCHEDULE')
  };

  constructor(props) {
    super(props);

    this.state = {
      date: props.defaultDate || moment().add(LIMIT_HRS, 'hours').add(DEFAULT_BUFFER_HOURS, 'hours').startOf('minute'),
      errors: ''
    };
  }

  timeValidator = (time) => {
    const result = {
      valid: false,
      errors: ''
    };

    // check if time is {LIMIT_HRS} hour after current time
    if (time.isAfter(moment().add(LIMIT_HRS, 'hours'))) {
      result.valid = true;
    } else {
      if (LIMIT_HRS == 0) {
        // when the user picks time which is not valid and no restriction in limit hours.
        // the time is in the past
        result.errors = _('ERROR_NOT_IN_PAST');
      } else if (LIMIT_HRS == 1) {
        result.errors = _('ERROR_NOT_WITHIN_HOURS').replace('{}', LIMIT_HRS).replace('hours', 'hour');
      } else {
        result.errors = _('ERROR_NOT_WITHIN_HOURS').replace('{}', LIMIT_HRS);
      }
    }

    return result;
  }

  setTime = (date) => {
    // drop seconds
    date.startOf('minute');
    const { errors } = this.timeValidator(date);

    this.setState({
      date,
      errors
    });
  }

  handleScheduleClick = () => {
    const { onConfirm } = this.props;
    const { date } = this.state;
    const { valid, errors } = this.timeValidator(date);

    if (valid) {
      onConfirm(date.valueOf());
    } else {
      this.setState({ errors });
    }
  }

  render() {
    const {
      classes: c, onCancel, pages, versionNumber, position, ctaText
    } = this.props;
    const { date, errors } = this.state;

    let dialogModalStyle = {
      width: '500px',
      height: '280px',
      display: 'flex',
      justifyContent: 'center',
      position: 'absolute',
      zIndex: '10',
      right: '100px',
      top: '50px'
    };

    if (position) {
      dialogModalStyle = {
        ...dialogModalStyle,
        ...position
      };
    }

    return (
      <DialogModal
        arrowPosition="top"
        containerStyle={dialogModalStyle}
      >
        <div className={c.pageList}>
          <div className={c.modalTitle}>{_('PAGES')}</div>
          {pages && pages.map((p, i) => <div className={c.pagePath} key={i}>{getDisplayPath(p)}</div>)}
        </div>
        <div className={c.scheduler}>
          <div className={c.modalTitle}>{_('VERSION_NUMBER')}</div>
          <TextInput
            placeholder="x.x.x"
            value={versionNumber}
            disabled
            additionalStyles={{ width: '100px', marginBottom: '10px' }}
          />
          <div className={c.time}>
            <div>
              <div className={c.modalTitle}>
                {_('DATE')}
              </div>
              <CalendarPicker
                value={date}
                onChange={newDate => this.setTime(moment(newDate))}
              />
            </div>
            <div>
              <div className={c.modalTitle}>{_('TIME')}</div>
              <TimePicker
                className={c.timePicker}
                value={date}
                onChange={this.setTime}
                showSecond={false}
                use12Hours
              />
            </div>
          </div>
          <div className={c.errorContainer}>
            {errors &&
              <div className={c.error}>
                <span className="material-icons">error</span>
                {errors}
              </div>
            }
          </div>
          <div className={c.actions}>
            <Button secondary onClick={onCancel}>
              {_('CANCEL')}
            </Button>
            <Button
              primary
              onClick={this.handleScheduleClick}
              disabled={errors}
            >
              {ctaText}
            </Button>
          </div>
        </div>
      </DialogModal>
    );
  }
}

const styles = {
  pageList: {
    flexGrow: '1',
    borderRight: '1px solid #ddd',
    overflowY: 'auto',
    padding: '20px',
    [DARK]: { borderRightColor: S.line }
  },
  scheduler: {
    flexGrow: '2',
    padding: '20px'
  },
  modalTitle: {
    textTransform: 'uppercase',
    fontSize: '12px',
    fontWeight: '400',
    marginBottom: '5px'
  },
  pagePath: {
    paddingTop: '5px',
    wordBreak: 'break-all',
    whiteSpace: 'normal',
    lineHeight: '1.4',
    fontSize: '12px'
  },
  time: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '20px',
    marginTop: '20px',
    '& .react-datepicker__input-container': {
      position: 'relative',
      '&::after': {
        position: 'absolute',
        content: '"\\e878"',
        fontFamily: 'Material Icons',
        right: '0',
        top: '0',
        bottom: '0',
        fontSize: '20px',
        display: 'block',
        lineHeight: '38px',
        marginRight: '5px'
      }
    }
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8
  },
  timePicker: {
    position: 'relative',
    '&::after': {
      position: 'absolute',
      content: '"\\e192"',
      fontFamily: 'Material Icons',
      right: '0',
      top: '0',
      bottom: '0',
      fontSize: '20px',
      display: 'block',
      lineHeight: '38px',
      marginRight: '5px'
    },
    '& .rc-time-picker-input': {
      color: 'hsl(0, 0%, 20%)',
      width: '100%',
      border: '1px solid hsl(0, 0%, 90%)',
      padding: '10px 10px',
      outline: 'none',
      fontSize: '13px',
      fontWeight: '400',
      borderRadius: '3px',
      height: '38px'
    },
    [DARK]: { '& .rc-time-picker-input': { color: S.fg, borderColor: S.fieldLine, background: S.field } }
  },
  errorContainer: {
    marginBottom: '15px',
    height: '30px'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: 'hsl(0, 52%, 40%)',
    '& .material-icons': {
      fontSize: '20px',
      marginRight: '5px'
    },
    [DARK]: { color: S.red }
  }
};

export default injectSheet(styles)(Scheduler);
